import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FUENTE, useTema } from './tema';
import {
  publicarParaVer, publicarEnBiblioteca, bibliotecaConfigurada,
  urlEmpotrada, bajarDocumento, selloDeDocumento
} from '../../services/actaPublicada';
import { leerActa } from '../../services/minutasV2Service';
import {
  hayCambiosSinSubir, queEnsenar, hayQueOfrecerVer
} from './vistaDelDocumento';

// Se re-exporta porque antes vivia aqui y hay quien la importa de aqui.
export { hayCambiosSinSubir };

/**
 * VistaWord — editar el acta en Word, dentro de la página.
 *
 * Editar debe ser editar: se pulsa y aparece el documento escribible. Todo lo
 * demás —subirlo a OneDrive, traerlo de vuelta, poner al día la minuta y las
 * zonas— es fontanería y no tiene por qué salir a la superficie.
 *
 * Tres cosas hacen que se sienta inmediato, y ninguna es cosmética:
 *
 * 1. **Se sube en cuanto existe el acta, no cuando se pulsa.** Word para la web
 *    es un host WOPI: abre archivos, no bytes, así que el .docx tiene que estar
 *    en OneDrive antes. Subirlo al pulsar metía una espera de varios segundos
 *    justo en el momento en que la persona quería escribir. Se hace antes, de
 *    fondo, y al pulsar ya está.
 *
 * 2. **Los cambios vuelven solos.** Mientras el editor está abierto se mira el
 *    sello del archivo cada pocos segundos —no se descarga: eso serían megas
 *    por minuto para concluir casi siempre que nada cambió—. Cuando cambia y se
 *    queda quieto, se trae y se pone al día la aplicación.
 *
 * 3. **Se espera a que el sello se estabilice.** Word guarda por partes y en
 *    mitad de un volcado el archivo puede estar a medias. Traer el primer
 *    cambio que se ve daría a veces un acta incompleta, que es peor que tardar
 *    cuatro segundos más.
 *
 * El componente sigue montado aunque no se esté viendo: así la subida se
 * adelanta y, al cerrar el editor, todavía puede recoger lo último que Word
 * guardó con retraso.
 *
 * ── Quién manda ─────────────────────────────────────────────────────────
 *
 * El documento de OneDrive, con una excepción que no es un matiz: mientras
 * haya algo escrito en la aplicación que todavía no ha subido, manda la
 * aplicación.
 *
 * Sin la regla general habría dos versiones del acta y al descargar saldría
 * cualquiera de las dos. Sin la excepción, el archivo —que en ese momento es
 * una copia ATRASADA— vuelve y deshace la corrección que se acaba de escribir.
 * Eso pasaba: el detalle está en `sincronizar`.
 */

// Cada cuánto se pregunta por el sello mientras se edita. Cuatro segundos es
// corto para que los cambios aparezcan solos, y largo para que una hora de
// edición no sean miles de peticiones.
const LATIDO = 4000;

// Lo que se espera tras cerrar el editor antes de recoger lo último. Word puede
// tardar un momento en volcar el guardado final.
const ULTIMA_ESPERA = 3000;

// Cuánto antes de su hora se da por vencido el vale de entrada. Un minuto: lo
// que puede pasar entre mirarlo y que Office lo lea.
const MARGEN_VALE = 60 * 1000;

/**
 * ¿Se puede entrar todavía con esta sesión?
 *
 * Sin fecha de caducidad se da por buena: es lo que dijo el servicio, y pedir
 * una sesión nueva en cada pintado por no saberlo sería peor.
 */
const valeVigente = (u) => !u || !u.caduca || u.caduca - Date.now() > MARGEN_VALE;

// La barra azul que Office pone encima del documento —«OneDrive», el nombre del
// archivo, sus botones— vive dentro del recuadro, que es de otro dominio. Ni el
// CSS ni el JavaScript de esta página entran ahí, así que no se puede ocultar:
// lo que se hace es recortarla. La ventanilla mide lo que tiene que medir y el
// documento se sube por dentro, de modo que la barra queda fuera de lo que se
// ve. Si Office cambia el alto de su barra, se cambia este número.
const ALTO_BARRA_OFFICE = 48;

// El nombre del recuadro. Es como el formulario sabe dónde dejar la sesión de
// edición: `target` apunta a este nombre.
const NOMBRE_MARCO = 'acta-en-word';

const estilos = (C) => ({
  caja: { marginTop: 4 },
  barra: {
    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
    marginBottom: 8, fontSize: 12.5, color: C.tenue, lineHeight: 1.55, minHeight: 20
  },
  ventanilla: {
    width: '100%', height: '78vh', minHeight: 460, overflow: 'hidden',
    border: `1px solid ${C.borde}`, borderRadius: 8, background: '#ffffff'
  },
  marco: {
    display: 'block', width: '100%', border: 0, background: '#ffffff',
    // Más alto que su ventanilla y subido justo lo que mide la barra: lo que
    // sobra por arriba es lo que se recorta.
    height: `calc(100% + ${ALTO_BARRA_OFFICE}px)`,
    marginTop: -ALTO_BARRA_OFFICE
  },
  aviso: {
    padding: '14px 16px', borderRadius: 8, fontSize: 13, lineHeight: 1.6,
    border: `1px solid ${C.borde}`, background: C.panel, color: C.suave, maxWidth: 720
  },
  malo: { borderColor: C.atencion, color: C.texto },
  boton: {
    display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 13px',
    background: C.panel, border: `1px solid ${C.borde}`, borderRadius: 8,
    color: C.texto, fontSize: 13, fontFamily: FUENTE, cursor: 'pointer'
  },
  enlace: { color: C.acentoClaro },
  // Se cuenta, no se avisa: esto no es un problema, es el documento poniéndose
  // al día. En gris y sin adorno.
  calmado: { color: C.tenue }
});

/**
 * @param {Object} props
 * @param {string|null} props.base64 - el .docx generado
 * @param {string} props.titulo - el de la reunión, para el nombre del archivo
 * @param {Date|string|null} props.fecha - para distinguir sesiones de una misma reunión
 * @param {Function} props.tokenGraph - da un token de Microsoft Graph
 * @param {Function} props.onCambios - recibe el acta cuando vuelve editada
 * @param {Function} props.onDocumento - dice qué archivo hay subido, o null
 * @param {boolean} props.enBiblioteca - si va a la biblioteca del equipo
 * @param {boolean} props.leerContenido - si hay que leer las zonas de lo que vuelve
 * @param {boolean} props.activa - si el editor se está viendo ahora mismo
 * @param {React.ReactNode} props.children - qué enseñar cuando el Word no se ve
 */
const VistaWord = ({
  base64 = null, titulo = '', fecha = null, tokenGraph, onCambios = null,
  onDocumento = null, enBiblioteca = true, leerContenido = true,
  activa = true, children = null
}) => {
  const { C } = useTema();
  const S = useMemo(() => estilos(C), [C]);

  const [url, setUrl] = useState(null);
  const [archivo, setArchivo] = useState(null);
  const [estado, setEstado] = useState('inactivo');
  const [fallo, setFallo] = useState(null);
  const [sincronizando, setSincronizando] = useState(false);
  // Por qué hubo que recurrir a OneDrive, si hubo que hacerlo.
  const [reserva, setReserva] = useState(null);
  // El archivo estaba abierto en Word y no admitió la escritura.
  const [bloqueado, setBloqueado] = useState(false);

  // El documento que ya está arriba. Sin esto, cada repintado volvería a
  // subirlo.
  const publicado = useRef(null);
  // El que se está subiendo ahora mismo. `publicado` llega tarde: solo se marca
  // al terminar, y en ese hueco cabía una segunda subida en paralelo.
  const enCurso = useRef(null);
  // El sello del archivo la última vez que lo dejamos nosotros. Lo que venga
  // distinto de esto lo escribió una persona en Word.
  const sello = useRef(null);
  // Un sello nuevo visto una vez. Hasta que se repita no se da por terminado el
  // guardado.
  const visto = useRef(null);
  // Si hay una subida a medias. Mientras la hay, lo que esté en OneDrive es por
  // definición lo de antes.
  const subiendo = useRef(false);

  // El formulario que lleva la sesión de edición al recuadro.
  const formulario = useRef(null);
  // Qué sesión se envió ya. Reenviar el formulario recarga Word entero: se
  // perdería el cursor y, si Word no hubiera terminado de guardar, lo escrito.
  //
  // Guarda el objeto, no su dirección: la dirección es la del documento y no
  // cambia nunca, así que compararla decía «esta ya se envió» ante una sesión
  // recién pedida.
  const enviado = useRef(null);
  // La sesión que ya se intentó renovar. Sin esto, una renovación que falla se
  // reintenta en cada pintado.
  const renovado = useRef(null);

  // ── Cuándo se vuelve a SUBIR el acta ──────────────────────────────────
  //
  // Un respiro para juntar los cambios que llegan en ráfaga, de pasar por tres
  // campos seguidos con el tabulador: serían tres subidas a SharePoint para
  // acabar en el mismo sitio.
  //
  // Esto ya no decide cuándo se ve el documento —eso lo decide el botón, más
  // abajo—, solo cuándo se sube. Antes era lo uno y lo otro, y de ahí venía que
  // corregir campos recargara Word sin parar.
  //
  // La PRIMERA no espera: lo que se quiere al abrir una reunión es ver el acta,
  // no una cuenta atrás.
  const SOSIEGO = 1500;
  const [asentado, setAsentado] = useState(base64);

  useEffect(() => {
    if (base64 === asentado) return undefined;
    if (!publicado.current) { setAsentado(base64); return undefined; }
    const reloj = setTimeout(() => setAsentado(base64), SOSIEGO);
    return () => clearTimeout(reloj);
  }, [base64, asentado]);

  // ── Subir y enseñar son dos cosas distintas ───────────────────────────
  //
  // Hasta ahora iban juntas: en cuanto el acta se volvía a subir, el recuadro
  // se recargaba con ella. Y recargar el recuadro es recargar Word entero, que
  // tarda sus segundos y —esto es lo que lo hacía insufrible— se lleva el
  // cursor: el panel del documento está al lado de los campos que se corrigen,
  // así que la persona se quedaba escribiendo en ningún sitio a mitad de una
  // palabra.
  //
  // Se separan. SUBIR sigue siendo automático, y no por comodidad: «Enviar a
  // firmas» y el PDF se fabrican a partir del archivo que está arriba, de modo
  // que dejar de subirlo mandaría a firmar una versión atrasada sin que nadie
  // lo note. ENSEÑAR pasa a pedirse: el documento se queda quieto hasta que se
  // pulsa, y lo que hay nuevo se anuncia en el botón.
  //
  // La primera vez no se pide: al abrir una reunión lo que se quiere es ver el
  // acta, no un botón para verla.
  const [urlEnPantalla, setUrlEnPantalla] = useState(null);

  // Se pulsó «Ver el documento» mientras la subida iba por el camino. Se
  // atiende en cuanto termine, sin que haya que volver a pulsar.
  //
  // Dos copias de lo mismo, y hacen falta las dos: el estado para que el botón
  // se repinte, y la referencia para que `publicar` —que se recrea con sus
  // dependencias, y esta no está entre ellas— lea lo que se pidió hace un
  // instante y no lo que había cuando se creó.
  const [pedido, setPedido] = useState(false);
  const pedidoRef = useRef(false);
  const anotarPedido = useCallback((v) => { pedidoRef.current = v; setPedido(v); }, []);

  // Lo que está arriba y todavía no se ve en el recuadro.
  const hayMasNuevo = Boolean(url && urlEnPantalla && url !== urlEnPantalla);

  // Lo que se corrigió y todavía no ha subido.
  const porSubir = Boolean(base64 && asentado !== base64) || estado === 'subiendo';

  // Hay algo que mirar que no se está mirando.
  const porReflejar = hayQueOfrecerVer({
    url, enPantalla: urlEnPantalla, base64, asentado, subiendo: estado === 'subiendo'
  });

  const mostrarLoNuevo = useCallback(() => {
    // Si todavía está subiendo, se apunta y se atiende al terminar.
    if (porSubir && !hayMasNuevo) { anotarPedido(true); return; }
    anotarPedido(false);
    if (url) setUrlEnPantalla(url);
  }, [porSubir, hayMasNuevo, url, anotarPedido]);

  const publicar = useCallback(async () => {
    const cuerpo = asentado;
    if (!cuerpo || !tokenGraph) return;

    // Se reserva el turno ANTES de esperar nada.
    //
    // `publicado` solo se marca cuando la subida termina, y eso deja un hueco
    // de varios segundos en el que un repintado arrancaba otra subida en
    // paralelo. La segunda encontraba el archivo bloqueado por la primera,
    // fallaba, y caía a la copia de OneDrive: cada acta acababa duplicada, y
    // una de las dos copias fuera de la biblioteca del equipo. El guardia tiene
    // que cerrarse en el mismo turno en que se abre.
    if (enCurso.current === cuerpo) return;
    enCurso.current = cuerpo;
    subiendo.current = true;

    setEstado('subiendo');
    setFallo(null);
    try {
      const token = await tokenGraph();

      // ── Dónde vive el acta que se edita ──────────────────────────────
      //
      // En la biblioteca del equipo, si está configurada. No es una
      // preferencia: el documento de la copia privada en OneDrive no daba
      // sesión de edición —Graph devolvía `postUrl` vacío—, y además tener el
      // acta en el sitio donde el equipo la busca es donde debe estar.
      //
      // Si publicar ahí falla se sigue con la copia de OneDrive: quedarse sin
      // nada que enseñar es peor que enseñarlo donde no toca, y el aviso lo
      // dice.
      // Las plantillas no van ahí: la biblioteca es donde el equipo busca
      // actas, y llenarla de machotes la estropea. Su sitio es el OneDrive de
      // quien las edita, que es quien las mantiene.
      const aLaBiblioteca = enBiblioteca && Boolean(bibliotecaConfigurada());
      let item;
      if (aLaBiblioteca) {
        try {
          item = await publicarEnBiblioteca(token, { base64: cuerpo, titulo, fecha });
        } catch (e) {
          item = null;
          setReserva(e.mensajeUsuario || e.message || 'No se pudo publicar en SharePoint.');
        }
      }
      if (!item) item = await publicarParaVer(token, { base64: cuerpo, titulo, fecha });

      const vista = await urlEmpotrada(token, item, { editable: true });

      // Si el archivo estaba bloqueado, lo de arriba NO se escribió: lo que hay
      // en SharePoint sigue siendo lo anterior. No se da por publicado —si no,
      // no se reintentaría nunca— y se avisa, porque una corrección que no llega
      // y no se nota es lo peor que puede pasar aquí.
      setBloqueado(Boolean(item.bloqueado));
      if (!item.bloqueado) publicado.current = cuerpo;
      // El sello de lo que acabamos de escribir: es la referencia contra la que
      // se compara. Sin él, nuestra propia subida parecería una edición ajena y
      // se traería de vuelta en el primer latido.
      sello.current = await selloDeDocumento(token, item).catch(() => null);
      visto.current = null;

      setArchivo(item);
      setUrl(vista);
      // La primera se enseña sin pedirla; las demás, solo si se pidieron. Se
      // usa el valor de dentro del `set` y no `urlEnPantalla` suelto: esta
      // función se recrea con sus dependencias y la de pantalla no está entre
      // ellas, así que leerla aquí daría la de hace un rato.
      setUrlEnPantalla(prev => queEnsenar({
        enPantalla: prev, nueva: vista, pedido: pedidoRef.current
      }));
      if (pedidoRef.current) anotarPedido(false);
      setEstado('listo');
    } catch (e) {
      // Se suelta el turno: si no, «Reintentar» no volvería a intentar nada.
      enCurso.current = null;
      // Y se suelta la petición de ver lo nuevo. Si no, quien pulsó «Ver el
      // documento» mientras subía se quedaba con el botón apagado diciendo «en
      // cuanto suba, se enseña…» para siempre: la subida que iba a atenderla
      // ya no va a llegar. Soltándola, el botón vuelve y se puede reintentar.
      anotarPedido(false);
      setFallo(e.message || 'No se pudo abrir el acta en Word.');
      setEstado('error');
    } finally {
      subiendo.current = false;
    }
  }, [asentado, titulo, fecha, tokenGraph, enBiblioteca, anotarPedido]);

  const hayLocalSinSubir = useCallback(
    () => hayCambiosSinSubir({ subiendo: subiendo.current, publicado: publicado.current, base64 }),
    [base64]
  );

  // Se sube en cuanto hay acta, se esté viendo o no: es lo que hace que pulsar
  // «Editar» sea inmediato en vez de una espera.
  useEffect(() => {
    if (!asentado) return;
    if (publicado.current === asentado) return;
    publicar();
  }, [asentado, publicar]);

  // Enviar el formulario es lo que carga Word escribible. Se hace una sola vez
  // por sesión: cada envío recarga el editor desde cero.
  //
  // ── Por qué se compara el objeto y no la dirección ─────────────────
  //
  // Graph devuelve dos cosas distintas: una dirección, que es la del documento
  // y es siempre la misma, y unos parámetros con el vale de entrada, que son
  // nuevos cada vez. Comparando la dirección, una sesión recién pedida parecía
  // la de antes y no se enviaba nada — y como el recuadro sí se había vuelto a
  // montar, se quedaba en blanco.
  //
  // Pasaba en cuanto el acta volvía a subirse sin salir de la vista previa, que
  // es lo que ocurre cada vez que vuelven cambios de Word. De ahí que fallara
  // «después de unas cuantas veces» y no a la primera.
  //
  // `estado` está en las dependencias por lo mismo: mientras se vuelve a subir,
  // el formulario no está en pantalla. Sin rehacer el efecto al volver a
  // 'listo', nadie enviaría la sesión nueva.
  //
  // Y envia lo que esta EN PANTALLA, no lo ultimo que se subio: esa es la
  // diferencia entre que el documento se recargue solo y que se recargue
  // cuando alguien lo pide.
  useEffect(() => {
    if (!activa || estado !== 'listo' || !urlEnPantalla || urlEnPantalla.modo !== 'post') return undefined;

    if (enviado.current !== urlEnPantalla && formulario.current) {
      enviado.current = urlEnPantalla;
      formulario.current.submit();
    }

    // Al dejar de verse, el recuadro se desmonta y se lleva el documento
    // cargado. Se olvida lo enviado para que al volver se cargue otra vez; sin
    // esto, salir de la edición y regresar dejaba el recuadro en blanco.
    return () => { enviado.current = null; };
  }, [activa, estado, urlEnPantalla]);

  // ── Quién más necesita saber dónde está el archivo ────────────────────
  //
  // Exportar a PDF. El PDF bueno no se fabrica fotografiando el informe: se le
  // pide a Office que convierta el documento, y para eso hay que decirle cuál.
  //
  // La condición es que lo subido sea lo que se está viendo. Si el acta cambió
  // y todavía se está subiendo —o si el archivo estaba bloqueado y la subida no
  // llegó a escribirse— se dice que no hay nada: más vale un PDF sin plantilla
  // que un PDF impecable de la versión anterior, porque el segundo no se nota.
  useEffect(() => {
    if (!onDocumento) return;
    const alDia = estado === 'listo' && archivo && publicado.current === base64;
    onDocumento(alDia ? archivo : null);
  }, [archivo, estado, base64, onDocumento]);

  /**
   * Pide una sesión de edición nueva para el mismo documento.
   *
   * No vuelve a subir nada: el archivo ya está arriba y es el bueno. Lo que se
   * renueva es el permiso de entrar, que es lo único que caduca.
   */
  const renovar = useCallback(async () => {
    if (!archivo || !tokenGraph) return;
    try {
      const token = await tokenGraph();
      const vale = await urlEmpotrada(token, archivo, { editable: true });
      setUrl(vale);
      // Tambien la de pantalla, y aqui si toca recargar: la sesion que se
      // estaba mirando esta muerta, y una sesion muerta no da un error, da un
      // recuadro en blanco. Conservarla por no recargar seria conservar nada.
      setUrlEnPantalla(vale);
    } catch (e) {
      setFallo(e.mensajeUsuario || e.message || 'Caduco la sesion de Word y no se pudo renovar.');
    }
  }, [archivo, tokenGraph]);

  // ── El vale caduca, y caducado no avisa ───────────────────────────────
  //
  // La sesión se pide una sola vez, al subir el acta, y dura unos minutos.
  // Quien dejaba la vista previa abierta un rato, se iba a corregir campos y
  // volvía, entraba con un vale muerto: Office no da un error, devuelve un
  // recuadro vacío. Se mira antes de enseñar el documento y, si está vencido o
  // a punto, se pide otro.
  useEffect(() => {
    if (!activa || estado !== 'listo' || !urlEnPantalla) return;
    if (valeVigente(urlEnPantalla)) return;
    // Una sola renovación por sesión: si falla, no se entra en bucle.
    if (renovado.current === urlEnPantalla) return;
    renovado.current = urlEnPantalla;
    renovar();
  }, [activa, estado, urlEnPantalla, renovar]);

  /**
   * Trae lo escrito en Word y pone al día la aplicación.
   *
   * Se relee el documento entero en vez de adivinar qué cambió: el archivo es
   * la verdad, y deducir el resto de él es lo único que garantiza que la
   * pantalla, las zonas y la descarga digan lo mismo.
   */
  const sincronizar = useCallback(async () => {
    if (!archivo || !tokenGraph) return;

    // ── Lo de aquí manda sobre lo de allí mientras no haya subido ───────
    //
    // Este es el fallo que deshacía las correcciones solo, y conviene que
    // quede escrito porque desde fuera parecía que no se guardaban.
    //
    // Corregir una zona rehace el .docx, y subirlo tarda unos segundos. En ese
    // hueco el latido encontraba el archivo de OneDrive distinto del sello
    // —Word para la web lo reescribe al abrirlo, aunque nadie teclee— y lo
    // daba por «editado en Word». Entonces lo bajaba, lo leía, y guardaba ESO:
    // la versión de ANTES de la corrección, encima de la corrección, un minuto
    // después. En el historial se veía «Corregida a mano» y acto seguido
    // «Editada en Word».
    //
    // Así que mientras haya algo escrito aquí que no esté arriba, no se adopta
    // nada: le toca subir, no bajar. El latido volverá a pasar.
    if (hayLocalSinSubir()) return;

    setSincronizando(true);
    try {
      const token = await tokenGraph();
      const nuevo = await bajarDocumento(token, archivo);

      // El sello cambió pero el contenido no: pasa cuando Word reescribe
      // metadatos al cerrar. No hay nada que contarle a nadie.
      if (nuevo === publicado.current) {
        sello.current = await selloDeDocumento(token, archivo).catch(() => sello.current);
        visto.current = null;
        return;
      }

      // Una plantilla no tiene zonas que leer —tiene huecos, y de eso se
      // encarga el servidor al guardarla—. Pedir su lectura sería una llamada
      // de más y un motivo de fallo donde no hace falta ninguno.
      const leido = leerContenido ? await leerActa({ contentBase64: nuevo }) : {};

      // Se marca como publicado ANTES de avisar: si no, el cambio que provoca
      // el aviso dispararía otra subida del documento recién bajado, machacando
      // en OneDrive lo que la persona acababa de escribir.
      publicado.current = nuevo;
      sello.current = await selloDeDocumento(token, archivo).catch(() => sello.current);
      visto.current = null;

      if (onCambios) onCambios({ base64: nuevo, ...leido });
      setFallo(null);
    } catch (e) {
      setFallo(e.mensajeUsuario || e.message || 'No se pudieron traer los cambios de Word.');
    } finally {
      setSincronizando(false);
    }
  }, [archivo, tokenGraph, onCambios, leerContenido, hayLocalSinSubir]);

  // `sincronizar` depende de `onCambios`, que quien usa este componente
  // redefine en cada pintado. Metido en las dependencias de los efectos de
  // abajo, el intervalo se destruiría y recrearía en cada render: el
  // temporizador se reiniciaría sin llegar a cumplir sus cuatro segundos y los
  // cambios no volverían nunca. Con la referencia, los efectos solo dependen de
  // lo que de verdad los define y siempre llaman a la versión más reciente.
  const ultimaSincronizacion = useRef(sincronizar);
  useEffect(() => { ultimaSincronizacion.current = sincronizar; }, [sincronizar]);

  // Mientras se edita, se vigila el archivo.
  useEffect(() => {
    if (!activa || estado !== 'listo' || !archivo) return undefined;

    let vivo = true;
    const latido = setInterval(async () => {
      try {
        const actual = await selloDeDocumento(await tokenGraph(), archivo);
        if (!vivo) return;

        if (actual === sello.current) { visto.current = null; return; }
        // Primera vez que se ve este sello: puede ser un guardado a medias.
        if (visto.current !== actual) { visto.current = actual; return; }
        // Dos latidos con el mismo sello: Word ha terminado de escribir.
        await ultimaSincronizacion.current();
      } catch {
        // Un fallo de red puntual no debe ensuciar la pantalla: se reintenta en
        // el siguiente latido.
      }
    }, LATIDO);

    return () => { vivo = false; clearInterval(latido); };
  }, [activa, estado, archivo, tokenGraph]);

  // Volver a esta pestaña es la señal más clara de que se terminó de escribir
  // en la otra. Esperar al siguiente latido dejaba unos segundos en los que la
  // minuta mostraba lo de antes — justo cuando la persona viene a comprobar que
  // sus cambios llegaron. Se mira en cuanto la pestaña recupera el foco.
  useEffect(() => {
    if (!activa || estado !== 'listo') return undefined;

    const alVolver = () => {
      if (document.visibilityState === 'visible') ultimaSincronizacion.current();
    };
    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('focus', alVolver);
    return () => {
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('focus', alVolver);
    };
  }, [activa, estado]);

  // Al cerrar el editor se recoge lo último: el guardado final de Word puede
  // llegar justo después de que la persona cambie de pestaña.
  useEffect(() => {
    if (activa || estado !== 'listo') return undefined;
    const t = setTimeout(() => { ultimaSincronizacion.current(); }, ULTIMA_ESPERA);
    return () => clearTimeout(t);
  }, [activa, estado]);

  // En cuanto el Word no se puede mostrar —no se está editando, aún se está
  // subiendo, o falló— se ve lo que venga dentro: el acta redactada. Poner en su
  // lugar un cartel de «cargando» dejaría la pantalla casi vacía justo al abrir
  // la reunión, que es cuando la persona quiere leer algo.
  if (!activa) return children;

  if (!base64) {
    return (
      <>
        <div style={S.aviso}>
          Para editar el acta en Word hace falta generarla antes con un formato: es el documento
          generado lo que se abre, no el informe redactado.
        </div>
        {children}
      </>
    );
  }

  // Los dos carteles de abajo son para cuando NO hay nada que enseñar todavía.
  //
  // Antes bastaba con que el estado no fuera «listo», y eso incluía cada
  // refresco: el recuadro se desmontaba, aparecía una línea de texto sobre el
  // hueco y volvía a montarse. Un documento que ya está en pantalla no se quita
  // para decir que se está actualizando — se deja, y se dice encima.
  if (estado === 'error' && !url) {
    return (
      <>
        <div style={{ ...S.aviso, ...S.malo }}>
          <p style={{ margin: '0 0 10px' }}>{fallo}</p>
          <button style={S.boton} onClick={publicar}>Reintentar</button>
        </div>
        {children}
      </>
    );
  }

  // Lo que decide si hay algo que ensenar es la sesion EN PANTALLA. Puede
  // haber ya una mas nueva subida y no ensenarse todavia: eso es justo lo que
  // se pretende, y no un estado de carga.
  if (!urlEnPantalla) {
    return (
      <>
        <div style={S.barra}><span>Abriendo el acta en Word…</span></div>
        {children}
      </>
    );
  }

  return (
    <div style={S.caja}>
      {/* Sin barra de estado.

          El Word es una vista: decir «así queda el documento» encima de un
          documento que se está viendo es contar lo que ya se ve. Lo que sí
          sigue apareciendo es lo que salió mal, porque eso no se ve mirando.

          El bloqueo, sobre todo: significa que una corrección guardada NO llegó
          al archivo, y quien no lo sepa se irá creyendo que sí. */}
      {/* `estado === 'subiendo'` ya no hace falta aqui: subir con algo en
          pantalla es `porReflejar`, y sin nada en pantalla no se llega hasta
          aqui. Dejarlo abria la barra vacia. */}
      {(bloqueado || reserva || fallo || sincronizando || porReflejar) && (
        <div style={S.barra}>
          {sincronizando && <span>Recogiendo lo escrito en Word…</span>}

          {/* El boton, y no un refresco solo.

              Recargar el recuadro recarga Word entero: tarda, y se lleva el
              cursor de donde se estuviera escribiendo —los campos que se
              corrigen estan justo al lado—. Hacerlo en cada guardado convertia
              corregir diez campos en diez interrupciones.

              Asi que se avisa y se espera. Lo que se ve sigue siendo un acta de
              verdad, solo que la de hace un momento, y eso lo dice el boton.

              Se deja pulsable mientras sube: quien lo pulse ahi se queda
              apuntado y el documento aparece en cuanto este, sin tener que
              volver a mirar si ya. */}
          {porReflejar && (
            <button
              type="button"
              style={{ ...S.boton, ...(pedido ? S.calmado : null) }}
              onClick={mostrarLoNuevo}
              disabled={pedido}
              title="Vuelve a cargar el documento con lo ultimo que has corregido"
            >
              <span aria-hidden="true">⟳</span>
              {pedido
                ? 'En cuanto suba, se enseña…'
                : (porSubir ? 'Ver el documento (acabando de guardar)' : 'Ver el documento al día')}
            </button>
          )}
          {porReflejar && (
            <span style={S.calmado}>
              Lo que se ve es de antes de tus últimas correcciones.
            </span>
          )}

          {bloqueado && (
            <span style={{ color: C.atencion }}>
              Estaba abierto en Word: se muestra el documento tal como está guardado. Si venías de
              corregir campos, eso todavía no se ha escrito — cierra Word y vuelve a guardarlo.
            </span>
          )}

          {reserva && (
            <span style={{ color: C.atencion }}>
              En tu OneDrive, no en la biblioteca: {reserva}
            </span>
          )}

          {/* Con el documento ya en pantalla, un fallo al actualizarlo se
              contaba y nada mas: no habia manera de reintentar sin recargar la
              pagina entera. El boton de «Reintentar» solo existia en el caso de
              que no hubiera nada que ensenar. */}
          {fallo && (
            <>
              <span style={{ color: C.atencion }}>{fallo}</span>
              <button type="button" style={S.boton} onClick={publicar}>Reintentar</button>
            </>
          )}
        </div>
      )}

      {/* La sesión de edición no cabe en una dirección: viaja en los parámetros
          de un POST. Por eso hay un formulario y no un `src` a secas — con el
          `src` el documento salía, pero en solo lectura. El formulario apunta al
          recuadro por su nombre y se envía solo al montarse. */}
      {urlEnPantalla.modo === 'post' && (
        <form
          ref={formulario}
          action={urlEnPantalla.url}
          method="post"
          target={NOMBRE_MARCO}
          style={{ display: 'none' }}
        >
          {urlEnPantalla.parametros.map(([nombre, valor]) => (
            <input key={nombre} type="hidden" name={nombre} value={valor} readOnly />
          ))}
        </form>
      )}

      {/* La ventanilla recorta la barra de Office. Ver arriba. */}
      <div style={S.ventanilla}>
        <iframe
          title="El acta en Word"
          name={NOMBRE_MARCO}
          // Con POST el contenido lo trae el formulario; poner también un `src`
          // cargaría el documento dos veces y la segunda pisaría la sesión buena.
          src={urlEnPantalla.modo === 'post' ? undefined : urlEnPantalla.url}
          style={S.marco}
          // Word para la web necesita ejecutar su propio código y abrir ventanas
          // para sus menús; sin esto el recuadro sale en blanco.
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms allow-downloads allow-modals allow-storage-access-by-user-activation"
        />
      </div>
    </div>
  );
};

export default VistaWord;
